import React, { useEffect, useMemo, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { api, apiErrorMessage, uploadFile } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { RichTextEditor } from "@/components/RichTextEditor";
import { PlatformIcon } from "@/components/campaign/PlatformIcon";
import {
  ArrowLeft,
  Info,
  Type as TypeIcon,
  Building2,
  AlignLeft,
  Layers,
  Tag,
  Wallet,
  Gauge,
  Eye,
  Target,
  CircleDollarSign,
  DollarSign,
  Sparkles,
  Trophy,
  Flame,
  Zap,
  Image as ImageIcon,
  Upload,
  Trash2,
  Link as LinkIcon,
  Music,
  Wand2,
  BadgeCheck,
  Plus,
  Eye as EyeIcon,
  Pause,
  Play,
  CheckCircle2,
  X as XIcon,
  CalendarDays,
} from "lucide-react";

const STATUSES = ["active", "paused", "ended"];
const PLATFORMS = ["TikTok", "Instagram", "YouTube"];
const CATEGORIES = ["Gaming", "Entertainment", "Music", "IRL", "Finance", "Podcasts", "Memes", "Lifestyle", "Other"];
const CAMPAIGN_TYPES = ["Logo", "Clipping", "Music", "Movies"];

const PAYMENT_OPTIONS = [
  { key: "cashapp", label: "Cash App", color: "from-emerald-500/30 to-emerald-500/5", text: "text-emerald-700 dark:text-emerald-300" },
  { key: "paypal", label: "PayPal", color: "from-sky-500/30 to-sky-500/5", text: "text-sky-700 dark:text-sky-300" },
  { key: "venmo", label: "Venmo", color: "from-indigo-500/30 to-indigo-500/5", text: "text-indigo-700 dark:text-indigo-300" },
  { key: "crypto", label: "Crypto", color: "from-amber-500/30 to-amber-500/5", text: "text-amber-700 dark:text-amber-300" },
];

const BONUS_OPTIONS = [
  { key: "viral", label: "Viral Clip", desc: "Reward over-performing clips", icon: Zap, accent: "text-rose-500" },
  { key: "leaderboard", label: "Leaderboard", desc: "Bonus for top ranks", icon: Trophy, accent: "text-amber-500" },
  { key: "streak", label: "Upload Streak", desc: "Reward daily uploaders", icon: Flame, accent: "text-orange-500" },
];

const SNAKE_PRESETS = [
  { name: "Neon Lime", color: "#c8ff00", speed: 3.5, brightness: 1 },
  { name: "Purple Glow", color: "#a855f7", speed: 4, brightness: 0.9 },
  { name: "Cyber Blue", color: "#22d3ee", speed: 3, brightness: 1.1 },
  { name: "Subtle White", color: "#ffffff", speed: 5, brightness: 0.5 },
];

// ---------- Reusable Section ----------
function Section({ id, icon: Icon, title, description, children, testId }) {
  return (
    <section
      id={id}
      data-testid={testId}
      className="rounded-2xl border border-zinc-200/80 bg-white/85 p-6 shadow-[0_1px_0_rgba(0,0,0,0.02),0_8px_24px_-12px_rgba(15,23,42,0.08)] backdrop-blur-sm transition-colors dark:border-white/[0.06] dark:bg-white/[0.025] dark:shadow-[0_1px_0_rgba(255,255,255,0.02),0_8px_24px_-12px_rgba(0,0,0,0.6)]"
    >
      <header className="mb-5 flex items-start gap-3">
        <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-700 dark:bg-white/[0.05] dark:text-zinc-300">
          <Icon size={18} />
        </span>
        <div className="min-w-0">
          <h2 className="text-[18px] font-bold tracking-tight text-zinc-900 dark:text-white sm:text-[20px]">{title}</h2>
          {description ? <p className="mt-0.5 text-[13px] text-zinc-500 dark:text-zinc-400">{description}</p> : null}
        </div>
      </header>
      <div>{children}</div>
    </section>
  );
}

function Field({ icon: Icon, label, hint, children }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
        {Icon ? <Icon size={13} className="opacity-70" /> : null}
        {label}
      </label>
      {children}
      {hint ? <p className="text-[11px] text-zinc-500 dark:text-zinc-500">{hint}</p> : null}
    </div>
  );
}

function StatInput({ icon: Icon, label, prefix, value, onChange, placeholder, testId }) {
  return (
    <label className="block cursor-text rounded-xl border border-zinc-200 bg-white p-3 transition-all hover:border-zinc-300 focus-within:border-[#c8ff00]/60 focus-within:shadow-[0_0_0_3px_rgba(200,255,0,0.08)] dark:border-white/[0.06] dark:bg-white/[0.02] dark:hover:border-white/[0.12] dark:focus-within:border-[#c8ff00]/40">
      <div className="flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wider text-zinc-500 dark:text-zinc-500">
        {Icon ? <Icon size={11} /> : null}
        {label}
      </div>
      <div className="mt-1.5 flex items-baseline gap-1">
        {prefix ? <span className="text-base font-semibold text-zinc-500 dark:text-zinc-500">{prefix}</span> : null}
        <input
          data-testid={testId}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full bg-transparent text-xl font-bold tabular-nums tracking-tight text-zinc-900 outline-none placeholder:text-zinc-300 dark:text-white dark:placeholder:text-white/15"
        />
      </div>
    </label>
  );
}

function Chip({ active, onClick, icon, children, testId }) {
  return (
    <button
      type="button"
      data-testid={testId}
      onClick={onClick}
      className={[
        "inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-[12.5px] font-medium transition-all",
        active
          ? "border-[#c8ff00]/50 bg-[#c8ff00]/15 text-[#2d3d00] shadow-[0_0_14px_-4px_rgba(200,255,0,0.4)] dark:bg-[#c8ff00]/[0.12] dark:text-[#d6ff5c] dark:shadow-[0_0_14px_-4px_rgba(200,255,0,0.25)]"
          : "border-zinc-200 bg-white text-zinc-600 hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/[0.08] dark:bg-white/[0.02] dark:text-zinc-400 dark:hover:border-white/[0.16] dark:hover:bg-white/[0.05]",
      ].join(" ")}
    >
      {icon}
      {children}
    </button>
  );
}

function SegmentedButtons({ options, value, onChange, testIdPrefix }) {
  return (
    <div className="inline-flex flex-wrap gap-1 rounded-xl border border-zinc-200 bg-zinc-50 p-1 dark:border-white/[0.06] dark:bg-white/[0.03]">
      {options.map((opt) => {
        const active = value === opt;
        return (
          <button
            key={opt}
            type="button"
            data-testid={`${testIdPrefix}-${opt.toLowerCase()}`}
            onClick={() => onChange(opt)}
            className={[
              "rounded-lg px-3.5 py-1.5 text-[12.5px] font-semibold transition-all",
              active
                ? "bg-[#c8ff00] text-black shadow-[0_0_10px_-2px_rgba(200,255,0,0.45)]"
                : "text-zinc-600 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white",
            ].join(" ")}
          >
            {opt}
          </button>
        );
      })}
    </div>
  );
}

function PaymentCard({ option, active, onToggle }) {
  return (
    <button
      type="button"
      data-testid={`campaign-payment-${option.key}`}
      onClick={onToggle}
      className={[
        "group relative flex items-center gap-2.5 rounded-xl border px-3 py-2.5 text-left transition-all",
        active
          ? `border-[#c8ff00]/50 bg-gradient-to-br ${option.color} shadow-[0_0_18px_-6px_rgba(200,255,0,0.35)]`
          : "border-zinc-200 bg-white hover:border-zinc-300 dark:border-white/[0.06] dark:bg-white/[0.02] dark:hover:border-white/[0.12]",
      ].join(" ")}
    >
      <span className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-white/80 ${option.text} shadow-sm dark:bg-white/[0.06]`}>
        <CircleDollarSign size={14} />
      </span>
      <span className="text-[12.5px] font-semibold text-zinc-800 dark:text-zinc-100">{option.label}</span>
      {active ? <CheckCircle2 size={14} className="ml-auto text-[#3a4d00] dark:text-[#c8ff00]" /> : null}
    </button>
  );
}

function BonusCard({ option, active, onToggle, amount, onAmount }) {
  const Icon = option.icon;
  return (
    <div
      className={[
        "relative rounded-xl border p-3.5 transition-all",
        active
          ? "border-[#c8ff00]/45 bg-[#c8ff00]/[0.06] shadow-[0_0_18px_-6px_rgba(200,255,0,0.3)]"
          : "border-zinc-200 bg-white dark:border-white/[0.06] dark:bg-white/[0.02]",
      ].join(" ")}
    >
      <div className="flex items-start gap-2.5">
        <span className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-100 ${option.accent} dark:bg-white/[0.05]`}>
          <Icon size={15} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[13px] font-semibold text-zinc-900 dark:text-white">{option.label}</div>
          <div className="text-[11px] text-zinc-500 dark:text-zinc-500">{option.desc}</div>
        </div>
        <Switch
          data-testid={`campaign-bonus-${option.key}`}
          checked={active}
          onCheckedChange={onToggle}
          className="shrink-0 data-[state=checked]:bg-[#c8ff00]"
        />
      </div>
      {active ? (
        <div className="mt-3 flex items-center gap-2 rounded-lg border border-zinc-200 bg-zinc-50 px-2.5 py-1.5 dark:border-white/[0.06] dark:bg-white/[0.025]">
          <DollarSign size={12} className="text-zinc-400" />
          <input
            data-testid={`campaign-${option.key}-bonus-input`}
            value={amount}
            onChange={(e) => onAmount(e.target.value)}
            placeholder="0"
            className="w-full bg-transparent text-[13px] font-semibold tabular-nums text-zinc-900 outline-none placeholder:text-zinc-300 dark:text-white dark:placeholder:text-white/15"
          />
          <span className="text-[10px] uppercase tracking-wider text-zinc-400">USD</span>
        </div>
      ) : null}
    </div>
  );
}

function DropZone({ label, hint, preview, onFile, accept = "image/*,image/gif", testId, height = "h-44" }) {
  const [drag, setDrag] = useState(false);
  return (
    <label
      data-testid={testId}
      onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDrag(false);
        const f = e.dataTransfer.files?.[0];
        if (f) onFile(f);
      }}
      className={[
        "group relative block cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed transition-all",
        height,
        drag
          ? "border-[#c8ff00] bg-[#c8ff00]/10"
          : "border-zinc-300 bg-zinc-50 hover:border-zinc-400 hover:bg-zinc-100/60 dark:border-white/[0.08] dark:bg-white/[0.02] dark:hover:border-white/[0.18] dark:hover:bg-white/[0.04]",
      ].join(" ")}
    >
      {preview ? (
        <>
          <img src={preview} alt="" className="absolute inset-0 h-full w-full object-cover transition-transform duration-300 group-hover:scale-105" />
          <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent opacity-0 transition-opacity group-hover:opacity-100" />
          <div className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 text-[11.5px] font-semibold text-white opacity-0 transition-opacity group-hover:opacity-100">
            <Upload size={12} /> Replace image
          </div>
        </>
      ) : (
        <div className="flex h-full flex-col items-center justify-center gap-1.5 text-zinc-500 dark:text-zinc-400">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-sm dark:bg-white/[0.05]">
            <Upload size={15} />
          </span>
          <div className="text-[13px] font-semibold">{label}</div>
          {hint ? <div className="text-[11px] text-zinc-400 dark:text-zinc-500">{hint}</div> : null}
        </div>
      )}
      <input type="file" accept={accept} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
    </label>
  );
}

// ---------- Live Preview Card ----------
function LivePreview({ state }) {
  const {
    name, creatorBrand, campaignType, payout, budget, paidOut, bannerPreview,
    streamerImageUrl, streamerVerified, verifiedBadgeUrl, progressColor, neonColor,
    snakeEnabled, snakeSpeed, snakeBrightness, status, platforms, bonusRewards,
  } = state;
  const pct = budget > 0 ? Math.min(100, (paidOut / budget) * 100) : 0;
  const snakeStyle = {
    "--neon": neonColor,
    "--snake-speed": `${snakeSpeed}s`,
    "--snake-brightness": String(snakeBrightness),
  };
  const dotColor = status === "active" ? "#22c55e" : status === "paused" ? "#eab308" : "#ef4444";
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-2">
        <div className="text-[12px] font-semibold uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Live Preview</div>
        <div className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] font-semibold text-emerald-700 dark:text-emerald-300">
          <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
          Updating
        </div>
      </div>

      <div
        data-testid="campaign-live-preview"
        className={snakeEnabled ? "snake-border" : ""}
        style={snakeEnabled ? snakeStyle : undefined}
      >
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-sm dark:border-white/[0.06] dark:bg-[#0a0a0a]">
          {/* Banner */}
          <div className="relative aspect-[16/9] w-full bg-zinc-100 dark:bg-white/[0.04]">
            {bannerPreview ? (
              <img src={bannerPreview} alt="" className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-zinc-300 dark:text-white/15">
                <ImageIcon size={32} />
              </div>
            )}
            <div className="absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-black/60 px-2 py-0.5 text-[10px] font-bold text-white backdrop-blur">
              ${Number(payout || 0).toFixed(2)} / 1K
            </div>
            <span className="absolute bottom-2 right-2 inline-block h-2 w-2 rounded-full shadow-[0_0_8px_currentColor]" style={{ background: dotColor, color: dotColor }} />
          </div>
          {/* Body */}
          <div className="space-y-2.5 p-3.5">
            <div className="flex items-center justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate text-[13.5px] font-bold text-zinc-900 dark:text-white">{name || "Campaign Name"}</div>
              </div>
              <span className="shrink-0 rounded-md bg-zinc-900 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-white dark:bg-white dark:text-black">
                {(campaignType || "Clipping").toUpperCase()}
              </span>
            </div>

            <div className="flex items-center gap-2">
              {streamerImageUrl ? (
                <img src={streamerImageUrl} alt="" className="h-5 w-5 rounded-full border border-zinc-200 object-cover dark:border-white/10" />
              ) : (
                <span className="h-5 w-5 rounded-full bg-zinc-200 dark:bg-white/10" />
              )}
              <span className="truncate text-[11.5px] font-medium text-zinc-600 dark:text-zinc-400">{creatorBrand || "Creator name"}</span>
              {streamerVerified ? (
                verifiedBadgeUrl ? (
                  <img src={verifiedBadgeUrl} alt="Verified" className="h-3 w-3 rounded-full object-cover" />
                ) : (
                  <BadgeCheck size={12} className="text-sky-500" />
                )
              ) : null}
            </div>

            <div className="flex items-center gap-2 text-[10.5px] text-zinc-500 dark:text-zinc-500">
              <span className="flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">$</span>
              <span>${Number(paidOut || 0).toFixed(0)} of ${Number(budget || 0).toFixed(0)} paid out</span>
              <span className="ml-auto font-semibold tabular-nums text-zinc-700 dark:text-zinc-300">{pct.toFixed(0)}%</span>
            </div>

            <div className="h-1.5 w-full overflow-hidden rounded-full bg-zinc-100 dark:bg-white/[0.06]">
              <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%`, background: progressColor }} />
            </div>

            <div className="flex items-center justify-between pt-1">
              <div className="flex gap-1">
                {platforms.length === 0 ? (
                  <span className="text-[10px] text-zinc-400">No platforms</span>
                ) : platforms.map((p) => (
                  <span key={p} className="flex h-5 w-5 items-center justify-center rounded-md bg-zinc-100 dark:bg-white/[0.05]">
                    <PlatformIcon platform={p.toLowerCase()} size={12} />
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                {bonusRewards.map((b) => (
                  <span key={b} className="rounded-md bg-[#c8ff00]/15 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[#3a4d00] dark:text-[#c8ff00]">{b}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Main Page ----------
export function CampaignEditorPage() {
  const { hasAccess, me } = useAuth();
  const { campaignId } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(campaignId);
  const canManage = hasAccess(["campaign_management"], ["create_campaigns", "manage_campaigns"]);
  const [initial, setInitial] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isEdit) return;
    api.get(`/campaigns/${campaignId}`).then((r) => setInitial(r.data)).catch((e) => setError(apiErrorMessage(e)));
  }, [campaignId, isEdit]);

  // ---------- Form state ----------
  const [name, setName] = useState("");
  const [creatorBrand, setCreatorBrand] = useState("");
  const [description, setDescription] = useState("");
  const [descriptionFormat, setDescriptionFormat] = useState("plain");
  const [platforms, setPlatforms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [campaignType, setCampaignType] = useState("Clipping");
  const [status, setStatus] = useState("active");

  const [payout, setPayout] = useState("0");
  const [threshold, setThreshold] = useState("0");
  const [targetViews, setTargetViews] = useState("0");
  const [minViews, setMinViews] = useState("1000");
  const [maxPayout, setMaxPayout] = useState("75");
  const [budget, setBudget] = useState("0");
  const [autoStop, setAutoStop] = useState(false);

  const [paymentMethods, setPaymentMethods] = useState(["cashapp", "paypal", "venmo", "crypto"]);
  const [bonusRewards, setBonusRewards] = useState([]);
  const [uploadStreakBonus, setUploadStreakBonus] = useState("0");
  const [viralClipBonus, setViralClipBonus] = useState("0");
  const [leaderboardBonus, setLeaderboardBonus] = useState("0");

  const [bannerUrl, setBannerUrl] = useState("");
  const [bannerDataUrl, setBannerDataUrl] = useState("");
  const [streamerImageUrl, setStreamerImageUrl] = useState("");
  const [streamerVerified, setStreamerVerified] = useState(true);
  const [verifiedBadgeUrl, setVerifiedBadgeUrl] = useState("");
  const [verifiedBadgeUploading, setVerifiedBadgeUploading] = useState(false);
  const [streamerUploading, setStreamerUploading] = useState(false);
  const [cardColor, setCardColor] = useState("");
  const [progressColor, setProgressColor] = useState("#c8ff00");

  const [tutorialVideos, setTutorialVideos] = useState([]);
  const [tutorialUploading, setTutorialUploading] = useState(false);
  const [links, setLinks] = useState([]);

  const [musicPlatform, setMusicPlatform] = useState("");
  const [musicTitle, setMusicTitle] = useState("");
  const [musicUrl, setMusicUrl] = useState("");

  const [neonColor, setNeonColor] = useState("#c8ff00");
  const [snakeEnabled, setSnakeEnabled] = useState(true);
  const [snakeSpeed, setSnakeSpeed] = useState(3.5);
  const [snakeBrightness, setSnakeBrightness] = useState(1);

  // Campaign Schedule (NEW)
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Hydrate from initial
  useEffect(() => {
    if (!initial) return;
    setName(initial.name || "");
    setCreatorBrand(initial.creator_brand || "");
    setDescription(initial.description || "");
    setDescriptionFormat(initial.description_format || "plain");
    setPlatforms(initial.platforms_allowed || []);
    setCategories(initial.categories || []);
    setCampaignType(initial.campaign_type || "Clipping");
    setStatus(initial.status || "active");
    setPayout(String(initial.payout_cpm ?? 0));
    setThreshold(String(initial.payout_threshold ?? 0));
    setTargetViews(String(initial.target_views ?? 0));
    setMinViews(String(initial.min_views ?? 1000));
    setMaxPayout(String(initial.max_payout_per_video ?? 75));
    setBudget(String(initial.budget ?? 0));
    setAutoStop(Boolean(initial.auto_stop_on_budget_end));
    setPaymentMethods(initial.payment_methods_allowed || ["cashapp", "paypal", "venmo", "crypto"]);
    setBonusRewards(initial.bonus_rewards || []);
    setUploadStreakBonus(String(initial.upload_streak_bonus ?? 0));
    setViralClipBonus(String(initial.viral_clip_bonus ?? 0));
    setLeaderboardBonus(String(initial.leaderboard_bonus ?? 0));
    setBannerUrl(initial.banner_url || "");
    setBannerDataUrl(initial.banner_image_data_url || "");
    setStreamerImageUrl(initial.streamer_image_url || "");
    setStreamerVerified(initial.streamer_verified !== false);
    setVerifiedBadgeUrl(initial.verified_badge_url || "");
    setCardColor(initial.card_color || "");
    setProgressColor(initial.progress_color || "#c8ff00");
    setTutorialVideos(initial.tutorial_videos || (initial.tutorial_video_url ? [{ id: initial.tutorial_video_file_id || "legacy", url: initial.tutorial_video_url, name: initial.tutorial_video_name || "Video Tutorial" }] : []));
    setLinks(initial.links || []);
    setMusicPlatform(initial.music_branding?.platform || "");
    setMusicTitle(initial.music_branding?.title || "");
    setMusicUrl(initial.music_branding?.url || "");
    setNeonColor(initial.neon_color || "#c8ff00");
    setSnakeEnabled(initial.snake_border_enabled !== false);
    setSnakeSpeed(Number(initial.snake_border_speed ?? 3.5));
    setSnakeBrightness(Number(initial.snake_border_brightness ?? 1));
    setStartDate(initial.start_date || "");
    setEndDate(initial.end_date || "");
  }, [initial]);

  function toggleArr(setter, value) {
    setter((prev) => prev.includes(value) ? prev.filter((x) => x !== value) : [...prev, value]);
  }

  async function readImageAsDataUrl(file) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.readAsDataURL(file);
    });
  }

  async function onBannerFile(file) {
    const dataUrl = await readImageAsDataUrl(file);
    setBannerDataUrl(dataUrl);
  }

  async function onStreamerFile(file) {
    setStreamerUploading(true);
    try {
      const res = await uploadFile(file, "campaign_streamer");
      const url = res?.url || res?.file_url || (res?.id ? `/api/files/${res.id}` : null);
      if (url) setStreamerImageUrl(url);
    } catch (e) {
      toast({ title: "Upload failed", description: apiErrorMessage(e) });
    } finally {
      setStreamerUploading(false);
    }
  }

  async function onVerifiedBadgeFile(file) {
    setVerifiedBadgeUploading(true);
    try {
      const res = await uploadFile(file, "campaign_verified_badge");
      const url = res?.url || res?.file_url || (res?.id ? `/api/files/${res.id}` : null);
      if (url) setVerifiedBadgeUrl(url);
    } catch (e) {
      toast({ title: "Upload failed", description: apiErrorMessage(e) });
    } finally {
      setVerifiedBadgeUploading(false);
    }
  }

  async function onTutorialFile(file) {
    if (tutorialVideos.length >= 3) return;
    setTutorialUploading(true);
    try {
      const uploaded = await uploadFile(file, "campaign-tutorial");
      setTutorialVideos((prev) => [...prev.slice(0, 2), { id: uploaded.id, url: uploaded.file_url, name: uploaded.original_filename || file.name }]);
      toast({ title: "Tutorial uploaded" });
    } catch (e) {
      toast({ title: "Upload failed", description: apiErrorMessage(e) });
    } finally {
      setTutorialUploading(false);
    }
  }

  function applyPreset(p) {
    setNeonColor(p.color);
    setSnakeSpeed(p.speed);
    setSnakeBrightness(p.brightness);
    setSnakeEnabled(true);
  }

  async function save() {
    setSaving(true);
    setError(null);
    try {
      const payload = {
        name,
        creator_brand: creatorBrand,
        description,
        description_format: descriptionFormat,
        platforms_allowed: platforms,
        payout_cpm: Number(payout || 0),
        payout_threshold: Number(threshold || 0),
        target_views: parseInt(targetViews || "0", 10) || 0,
        status,
        banner_url: bannerUrl || null,
        banner_image_data_url: bannerDataUrl || null,
        card_color: cardColor || null,
        progress_color: progressColor || "#c8ff00",
        min_views: parseInt(minViews || "1000", 10) || 1000,
        max_payout_per_video: Number(maxPayout || 75),
        payment_methods_allowed: paymentMethods,
        bonus_rewards: bonusRewards,
        streamer_image_url: streamerImageUrl || null,
        streamer_verified: streamerVerified,
        verified_badge_url: verifiedBadgeUrl || null,
        upload_streak_bonus: Number(uploadStreakBonus || 0),
        viral_clip_bonus: Number(viralClipBonus || 0),
        leaderboard_bonus: Number(leaderboardBonus || 0),
        budget: Number(budget || 0),
        auto_stop_on_budget_end: autoStop,
        neon_color: neonColor || "#c8ff00",
        snake_border_enabled: snakeEnabled,
        snake_border_speed: Number(snakeSpeed || 3.5),
        snake_border_brightness: Number(snakeBrightness || 1),
        music_branding: (musicTitle.trim() || musicUrl.trim() || musicPlatform) ? { platform: musicPlatform || "other", title: musicTitle.trim(), url: musicUrl.trim() } : null,
        campaign_type: campaignType,
        links: links.filter((l) => l.title || l.url),
        categories,
        tutorial_video_file_id: tutorialVideos[0]?.id || null,
        tutorial_video_url: tutorialVideos[0]?.url || null,
        tutorial_video_name: tutorialVideos[0]?.name || null,
        tutorial_videos: tutorialVideos,
        start_date: startDate || null,
        end_date: endDate || null,
      };
      const res = isEdit ? await api.put(`/campaigns/${campaignId}`, payload) : await api.post("/campaigns", payload);
      toast({ title: isEdit ? "Campaign updated" : "Campaign created" });
      navigate(`/campaigns/${res.data?.id || campaignId}`);
    } catch (e) {
      setError(apiErrorMessage(e));
    } finally {
      setSaving(false);
    }
  }

  const bannerPreview = bannerDataUrl || bannerUrl;
  const paidOut = Number(initial?.budget_spent ?? 0);

  const previewState = useMemo(() => ({
    name, creatorBrand, campaignType, payout, budget: Number(budget || 0), paidOut, bannerPreview,
    streamerImageUrl, streamerVerified, verifiedBadgeUrl, progressColor, neonColor,
    snakeEnabled, snakeSpeed, snakeBrightness, status, platforms, bonusRewards,
  }), [name, creatorBrand, campaignType, payout, budget, paidOut, bannerPreview, streamerImageUrl, streamerVerified, verifiedBadgeUrl, progressColor, neonColor, snakeEnabled, snakeSpeed, snakeBrightness, status, platforms, bonusRewards]);

  if (!canManage) {
    return (
      <div data-testid="campaign-editor-locked" className="rounded-2xl border border-zinc-200 bg-white p-8 text-sm text-zinc-500 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-zinc-400">
        You don't have access to create campaigns.
      </div>
    );
  }
  if (isEdit && !initial && !error) {
    return <div data-testid="campaign-editor-loading" className="py-12 text-center text-sm text-zinc-500 dark:text-zinc-400">Loading campaign…</div>;
  }

  return (
    <div data-testid="campaign-editor-page" className="mx-auto w-full max-w-[1350px]">
      {/* Header */}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-3 border-b border-zinc-200/80 pb-5 dark:border-white/[0.06]">
        <div>
          <Button
            data-testid="campaign-editor-back"
            variant="ghost"
            className="mb-2 -ml-2 h-8 gap-1.5 px-2 text-[12px] font-medium text-zinc-500 hover:text-zinc-900 dark:text-zinc-400 dark:hover:text-white"
            onClick={() => navigate("/campaigns")}
          >
            <ArrowLeft size={13} /> Campaigns
          </Button>
          <h1 data-testid="campaign-editor-title" className="text-2xl font-bold tracking-tight text-zinc-900 dark:text-white sm:text-[28px]">
            {isEdit ? "Edit Campaign" : "Create Campaign"}
          </h1>
          <p className="mt-1 text-[13.5px] text-zinc-500 dark:text-zinc-400">
            Build a premium clipping campaign with categories, rewards, branding and live preview.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid="campaign-editor-cancel"
            variant="ghost"
            onClick={() => navigate("/campaigns")}
            className="h-10 rounded-full px-5 text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-white/[0.05]"
          >
            Cancel
          </Button>
          <Button
            data-testid="campaign-save-button"
            disabled={saving || !name || !creatorBrand}
            onClick={save}
            className="h-10 rounded-full bg-[#c8ff00] px-5 font-bold text-black hover:bg-[#b8ef00] active:scale-[0.97]"
          >
            {saving ? "Saving…" : isEdit ? "Save Changes" : "Create Campaign"}
          </Button>
        </div>
      </div>

      {error ? (
        <div data-testid="campaign-editor-error" className="mb-5 rounded-xl border border-rose-500/20 bg-rose-500/10 px-4 py-3 text-sm text-rose-700 dark:text-rose-300">
          {error}
        </div>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-[1fr_360px]">
        {/* Main column */}
        <div className="space-y-5">
          {/* BASIC INFO */}
          <Section id="basic" icon={Info} title="Basic Info" description="Give your campaign a clear name and identity" testId="section-basic-info">
            <div className="grid gap-4 sm:grid-cols-2">
              <Field icon={TypeIcon} label="Campaign Name" hint="Shown on the public campaign card">
                <Input data-testid="campaign-name-input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Nike — Just Clip It" className="h-11 border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder:text-zinc-400 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
              </Field>
              <Field icon={Building2} label="Creator / Brand" hint="The streamer or brand running this">
                <Input data-testid="campaign-creator-input" value={creatorBrand} onChange={(e) => setCreatorBrand(e.target.value)} placeholder="e.g. Nike" className="h-11 border-zinc-200 bg-white text-[14px] text-zinc-900 placeholder:text-zinc-400 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
              </Field>
            </div>
            <div className="mt-4">
              <Field icon={AlignLeft} label="Description" hint="Markdown / rich text supported for admins">
                {me?.role === "admin" ? (
                  <div className="rounded-xl border border-zinc-200 bg-white p-1 dark:border-white/[0.08] dark:bg-white/[0.025]">
                    <RichTextEditor value={description} onChange={(v) => { setDescription(v); setDescriptionFormat("html"); }} />
                  </div>
                ) : (
                  <Textarea data-testid="campaign-description-input" value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Brief campaign details — what clippers should know…" className="min-h-[140px] resize-y border-zinc-200 bg-white text-[14px] leading-relaxed text-zinc-900 placeholder:text-zinc-400 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
                )}
              </Field>
            </div>
          </Section>

          {/* CAMPAIGN SETUP */}
          <Section id="setup" icon={Layers} title="Campaign Setup" description="Configure pay rate, budget and reach" testId="section-campaign-setup">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              <StatInput icon={Gauge} label="CPM" prefix="$" value={payout} onChange={setPayout} placeholder="8.00" testId="campaign-payout-input" />
              <StatInput icon={Wallet} label="Budget" prefix="$" value={budget} onChange={setBudget} placeholder="500" testId="campaign-budget-input" />
              <StatInput icon={Target} label="Threshold" prefix="$" value={threshold} onChange={setThreshold} placeholder="50" testId="campaign-threshold-input" />
              <StatInput icon={Eye} label="Min Views" value={minViews} onChange={setMinViews} placeholder="1000" testId="campaign-min-views-input" />
              <StatInput icon={CircleDollarSign} label="Max / Video" prefix="$" value={maxPayout} onChange={setMaxPayout} placeholder="75" testId="campaign-max-payout-input" />
              <StatInput icon={Eye} label="Target Views" value={targetViews} onChange={setTargetViews} placeholder="100000" testId="campaign-target-views-input" />
            </div>

            <div className="mt-5 space-y-4">
              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
                  <Layers size={13} className="opacity-70" /> Platforms Allowed
                </div>
                <div data-testid="campaign-platforms" className="flex flex-wrap gap-1.5">
                  {PLATFORMS.map((p) => (
                    <Chip key={p} active={platforms.includes(p)} onClick={() => toggleArr(setPlatforms, p)} testId={`campaign-platform-${p}`} icon={<PlatformIcon platform={p.toLowerCase()} size={13} />}>
                      {p}
                    </Chip>
                  ))}
                </div>
              </div>

              <div>
                <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
                  <Tag size={13} className="opacity-70" /> Categories
                </div>
                <div data-testid="campaign-categories" className="flex flex-wrap gap-1.5">
                  {CATEGORIES.map((cat) => (
                    <Chip key={cat} active={categories.includes(cat)} onClick={() => toggleArr(setCategories, cat)} testId={`campaign-category-${cat}`}>
                      {cat}
                    </Chip>
                  ))}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-3">
                <div className="text-[12px] font-medium text-zinc-600 dark:text-zinc-400">Campaign Type</div>
                <SegmentedButtons options={CAMPAIGN_TYPES} value={campaignType} onChange={setCampaignType} testIdPrefix="campaign-type" />
              </div>

              <div className="flex flex-wrap items-center gap-4 rounded-xl border border-zinc-200 bg-zinc-50/70 px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.025]">
                <div className="flex items-center gap-2">
                  <span className="text-[12px] font-medium text-zinc-600 dark:text-zinc-400">Status</span>
                  <SegmentedButtons options={STATUSES} value={status} onChange={setStatus} testIdPrefix="campaign-status" />
                </div>
                <label className="ml-auto flex items-center gap-2 text-[12.5px] text-zinc-600 dark:text-zinc-400">
                  <Checkbox data-testid="campaign-auto-stop-checkbox" checked={autoStop} onCheckedChange={(v) => setAutoStop(Boolean(v))} />
                  Auto-stop when budget ends
                </label>
              </div>

              {/* Schedule — Start / End dates */}
              <div className="rounded-xl border border-zinc-200 bg-zinc-50/70 px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.025]">
                <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
                  <CalendarDays size={13} className="opacity-70" /> Schedule <span className="text-[11px] font-normal text-zinc-500">(optional)</span>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500 dark:text-zinc-500">Start date</label>
                    <input
                      type="date"
                      data-testid="campaign-start-date-input"
                      value={startDate}
                      onChange={(e) => setStartDate(e.target.value)}
                      className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-[13px] text-zinc-900 outline-none focus:border-[#c8ff00] dark:border-white/[0.08] dark:bg-black dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-medium text-zinc-500 dark:text-zinc-500">End date</label>
                    <input
                      type="date"
                      data-testid="campaign-end-date-input"
                      value={endDate}
                      onChange={(e) => setEndDate(e.target.value)}
                      min={startDate || undefined}
                      className="w-full rounded-lg border border-zinc-300 bg-white px-3 py-2 text-[13px] text-zinc-900 outline-none focus:border-[#c8ff00] dark:border-white/[0.08] dark:bg-black dark:text-white"
                    />
                  </div>
                </div>
              </div>
            </div>
          </Section>

          {/* REWARDS */}
          <Section id="rewards" icon={DollarSign} title="Rewards & Payments" description="Pick payout methods and configure bonuses" testId="section-rewards">
            <div className="mb-5">
              <div className="mb-2 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">Payment Methods</div>
              <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
                {PAYMENT_OPTIONS.map((opt) => (
                  <PaymentCard key={opt.key} option={opt} active={paymentMethods.includes(opt.key)} onToggle={() => toggleArr(setPaymentMethods, opt.key)} />
                ))}
              </div>
            </div>
            <div>
              <div className="mb-2 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">Bonus Rewards</div>
              <div className="grid gap-2 sm:grid-cols-3">
                <BonusCard option={BONUS_OPTIONS[0]} active={bonusRewards.includes("viral")} onToggle={() => toggleArr(setBonusRewards, "viral")} amount={viralClipBonus} onAmount={setViralClipBonus} />
                <BonusCard option={BONUS_OPTIONS[1]} active={bonusRewards.includes("leaderboard")} onToggle={() => toggleArr(setBonusRewards, "leaderboard")} amount={leaderboardBonus} onAmount={setLeaderboardBonus} />
                <BonusCard option={BONUS_OPTIONS[2]} active={bonusRewards.includes("streak")} onToggle={() => toggleArr(setBonusRewards, "streak")} amount={uploadStreakBonus} onAmount={setUploadStreakBonus} />
              </div>
            </div>
          </Section>

          {/* BRANDING & ASSETS */}
          <Section id="branding" icon={ImageIcon} title="Branding & Assets" description="Upload banner and streamer avatar" testId="section-branding">
            <div className="grid gap-5 lg:grid-cols-[1fr_220px]">
              <div className="space-y-3">
                <Field icon={ImageIcon} label="Campaign Banner" hint="16:9 — used on the campaign card hero">
                  <DropZone label="Drag & drop or click to upload" hint="PNG, JPG, GIF up to 8 MB" preview={bannerPreview} onFile={onBannerFile} testId="campaign-banner-dropzone" />
                </Field>
                <Field label="Or paste an image URL">
                  <Input data-testid="campaign-banner-url-input" value={bannerUrl} onChange={(e) => setBannerUrl(e.target.value)} placeholder="https://…" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
                </Field>
              </div>
              <div className="space-y-3">
                <Field icon={BadgeCheck} label="Streamer Avatar" hint="Shown small on the card">
                  <div className="flex flex-col items-center gap-3">
                    <DropZone label={streamerUploading ? "Uploading…" : "Drop image"} preview={streamerImageUrl} onFile={onStreamerFile} testId="campaign-streamer-dropzone" height="h-32 w-32 !rounded-full" />
                    <Input data-testid="campaign-streamer-image-url-input" value={streamerImageUrl} onChange={(e) => setStreamerImageUrl(e.target.value)} placeholder="Or paste URL" className="h-9 border-zinc-200 bg-white text-[12px] text-zinc-900 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
                  </div>
                </Field>
                <label className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-zinc-50 px-3 py-2 text-[12.5px] text-zinc-700 dark:border-white/[0.06] dark:bg-white/[0.025] dark:text-zinc-200">
                  <Checkbox data-testid="campaign-streamer-verified-checkbox" checked={streamerVerified} onCheckedChange={(v) => setStreamerVerified(Boolean(v))} />
                  <BadgeCheck size={13} className="text-sky-500" /> Verified streamer
                </label>

                {streamerVerified && (
                  <Field icon={BadgeCheck} label="Verified Badge" hint="Optional custom badge image. Default uses the blue checkmark.">
                    <div className="flex items-center gap-3">
                      <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-zinc-200 bg-white dark:border-white/[0.08] dark:bg-white/[0.03]">
                        {verifiedBadgeUrl ? (
                          <img src={verifiedBadgeUrl} alt="Verified badge" className="h-9 w-9 rounded-full object-cover" />
                        ) : (
                          <BadgeCheck size={22} className="text-sky-500" />
                        )}
                      </div>
                      <div className="flex flex-1 flex-col gap-1.5">
                        <div className="flex items-center gap-1.5">
                          <label
                            data-testid="campaign-verified-badge-upload-label"
                            className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-lg border border-zinc-200 bg-white px-2.5 text-[12px] font-semibold text-zinc-700 transition-colors hover:border-[#c8ff00]/40 hover:bg-zinc-50 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-zinc-300 dark:hover:border-[#c8ff00]/25 dark:hover:bg-white/[0.06]"
                          >
                            <Upload size={12} />
                            {verifiedBadgeUploading ? "Uploading…" : verifiedBadgeUrl ? "Replace" : "Upload"}
                            <input
                              data-testid="campaign-verified-badge-input"
                              type="file"
                              accept="image/*"
                              className="hidden"
                              onChange={(e) => { const f = e.target.files?.[0]; if (f) onVerifiedBadgeFile(f); }}
                            />
                          </label>
                          {verifiedBadgeUrl ? (
                            <button
                              type="button"
                              data-testid="campaign-verified-badge-clear"
                              onClick={() => setVerifiedBadgeUrl("")}
                              className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-[12px] text-zinc-500 transition-colors hover:bg-zinc-100 hover:text-zinc-900 dark:hover:bg-white/[0.05] dark:hover:text-white"
                            >
                              <Trash2 size={12} /> Reset
                            </button>
                          ) : null}
                        </div>
                        <Input
                          data-testid="campaign-verified-badge-url-input"
                          value={verifiedBadgeUrl}
                          onChange={(e) => setVerifiedBadgeUrl(e.target.value)}
                          placeholder="Or paste image URL"
                          className="h-8 border-zinc-200 bg-white text-[12px] text-zinc-900 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white"
                        />
                      </div>
                    </div>
                  </Field>
                )}
              </div>
            </div>

            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              <Field label="Card Tint Color (optional)">
                <div className="flex items-center gap-2">
                  <input data-testid="campaign-card-color-input" type="color" value={cardColor || "#8B5CF6"} onChange={(e) => setCardColor(e.target.value)} className="h-10 w-12 cursor-pointer rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-white/[0.08] dark:bg-white/[0.02]" />
                  <Input data-testid="campaign-card-color-text" value={cardColor} onChange={(e) => setCardColor(e.target.value)} placeholder="#8B5CF6 or blank" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
                  {cardColor ? <Button variant="ghost" size="sm" onClick={() => setCardColor("")} className="h-8 text-zinc-500">Clear</Button> : null}
                </div>
              </Field>
              <Field label="Progress Bar Color">
                <div className="flex items-center gap-2">
                  <input data-testid="campaign-progress-color-input" type="color" value={progressColor || "#c8ff00"} onChange={(e) => setProgressColor(e.target.value)} className="h-10 w-12 cursor-pointer rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-white/[0.08] dark:bg-white/[0.02]" />
                  <Input value={progressColor} onChange={(e) => setProgressColor(e.target.value)} placeholder="#c8ff00" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.08] dark:bg-white/[0.03] dark:text-white" />
                </div>
              </Field>
            </div>
          </Section>

          {/* TUTORIALS & LINKS */}
          <Section id="tutorials" icon={PlayIconSafe} title="Tutorials & Links" description="Help clippers understand what you want" testId="section-tutorials">
            <Field label="Video Tutorials (up to 3)">
              <DropZone label={tutorialUploading ? "Uploading…" : "Drop a video or click to upload"} hint="MP4, WebM" preview={null} accept="video/*" onFile={onTutorialFile} testId="campaign-tutorial-upload" />
              {tutorialVideos.length > 0 ? (
                <div className="mt-3 grid gap-2 sm:grid-cols-3">
                  {tutorialVideos.map((vid, idx) => (
                    <div key={vid.id || idx} className="relative overflow-hidden rounded-xl border border-zinc-200 bg-black dark:border-white/[0.08]">
                      <button type="button" data-testid={`campaign-tutorial-delete-${idx}`} onClick={() => setTutorialVideos((prev) => prev.filter((_, i) => i !== idx))} className="absolute right-2 top-2 z-10 rounded-full bg-black/70 p-1.5 text-rose-300 transition-colors hover:bg-rose-500/30">
                        <Trash2 size={13} />
                      </button>
                      <video data-testid={`campaign-tutorial-preview-${idx}`} controls src={vid.url} className="aspect-video w-full bg-black" />
                      <div className="truncate px-3 py-1.5 text-[11px] text-zinc-300">{vid.name}</div>
                    </div>
                  ))}
                </div>
              ) : null}
            </Field>

            <div className="mt-5">
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
                  <LinkIcon size={13} className="opacity-70" /> Links
                </div>
                <Button type="button" variant="ghost" size="sm" onClick={() => setLinks([...links, { title: "", url: "" }])} className="h-7 gap-1 text-[12px] text-[#3a4d00] hover:bg-[#c8ff00]/10 dark:text-[#c8ff00]">
                  <Plus size={12} /> Add link
                </Button>
              </div>
              {links.length === 0 ? (
                <div className="rounded-xl border border-dashed border-zinc-200 bg-zinc-50/40 px-4 py-6 text-center text-[12px] text-zinc-500 dark:border-white/[0.06] dark:bg-white/[0.01] dark:text-zinc-500">
                  No links yet — add Discord, rules, or external pages.
                </div>
              ) : (
                <div className="space-y-2">
                  {links.map((link, idx) => (
                    <div key={idx} className="flex items-center gap-2 rounded-xl border border-zinc-200 bg-white p-2 transition-colors hover:border-zinc-300 dark:border-white/[0.06] dark:bg-white/[0.02] dark:hover:border-white/[0.12]">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-zinc-100 text-zinc-600 dark:bg-white/[0.05] dark:text-zinc-300">
                        <LinkIcon size={14} />
                      </span>
                      <Input value={link.title} onChange={(e) => { const copy = [...links]; copy[idx] = { ...copy[idx], title: e.target.value }; setLinks(copy); }} placeholder="Label" className="h-9 flex-1 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white" />
                      <Input value={link.url} onChange={(e) => { const copy = [...links]; copy[idx] = { ...copy[idx], url: e.target.value }; setLinks(copy); }} placeholder="https://…" className="h-9 flex-[1.5] border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white" />
                      <Button type="button" variant="ghost" size="icon" onClick={() => setLinks(links.filter((_, j) => j !== idx))} className="h-9 w-9 shrink-0 text-rose-500 hover:bg-rose-500/10">
                        <XIcon size={14} />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="mt-5 rounded-xl border border-zinc-200 bg-zinc-50/60 p-4 dark:border-white/[0.06] dark:bg-white/[0.025]">
              <div className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">
                <Music size={13} className="opacity-70" /> Music & Branding
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                <Select value={musicPlatform} onValueChange={setMusicPlatform}>
                  <SelectTrigger data-testid="campaign-music-platform" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white">
                    <SelectValue placeholder="Platform" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="tiktok">TikTok</SelectItem>
                    <SelectItem value="instagram">Instagram</SelectItem>
                    <SelectItem value="youtube">YouTube</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
                <Input data-testid="campaign-music-title" value={musicTitle} onChange={(e) => setMusicTitle(e.target.value)} placeholder="Title (e.g. Original sound)" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white" />
                <Input data-testid="campaign-music-url" value={musicUrl} onChange={(e) => setMusicUrl(e.target.value)} placeholder="https://…" className="h-10 border-zinc-200 bg-white text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white" />
              </div>
            </div>
          </Section>

          {/* ADVANCED EFFECTS */}
          <Section id="effects" icon={Wand2} title="Advanced Effects" description="Animated neon border that wraps the campaign card" testId="section-effects">
            <div className="flex items-center justify-between gap-3 rounded-xl border border-zinc-200 bg-zinc-50/60 px-4 py-3 dark:border-white/[0.06] dark:bg-white/[0.025]">
              <div className="flex items-center gap-2">
                <Sparkles size={14} className="text-[#3a4d00] dark:text-[#c8ff00]" />
                <div className="text-[13px] font-semibold text-zinc-900 dark:text-white">Snake Border</div>
                <span className="text-[11.5px] text-zinc-500 dark:text-zinc-400">A traveling neon glow around the card</span>
              </div>
              <Switch data-testid="campaign-snake-enabled" checked={snakeEnabled} onCheckedChange={setSnakeEnabled} className="data-[state=checked]:bg-[#c8ff00]" />
            </div>

            <div className={`mt-4 grid gap-5 transition-opacity lg:grid-cols-[1fr_220px] ${snakeEnabled ? "opacity-100" : "pointer-events-none opacity-50"}`}>
              <div className="space-y-4">
                <div>
                  <div className="mb-2 text-[12px] font-medium text-zinc-600 dark:text-zinc-400">Presets</div>
                  <div className="flex flex-wrap gap-1.5">
                    {SNAKE_PRESETS.map((p) => (
                      <button
                        key={p.name}
                        type="button"
                        onClick={() => applyPreset(p)}
                        className="group flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-[12px] font-medium text-zinc-700 transition-all hover:border-zinc-300 hover:bg-zinc-50 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-zinc-300 dark:hover:border-white/[0.16] dark:hover:bg-white/[0.05]"
                      >
                        <span className="h-2.5 w-2.5 rounded-full shadow-[0_0_8px_currentColor]" style={{ background: p.color, color: p.color }} />
                        {p.name}
                      </button>
                    ))}
                  </div>
                </div>

                <Field label="Color">
                  <div className="flex items-center gap-2">
                    <input data-testid="campaign-snake-color-picker" type="color" value={neonColor} onChange={(e) => setNeonColor(e.target.value)} className="h-10 w-12 cursor-pointer rounded-lg border border-zinc-200 bg-white p-0.5 dark:border-white/[0.08] dark:bg-white/[0.02]" />
                    <Input data-testid="campaign-snake-color-hex" value={neonColor} onChange={(e) => setNeonColor(e.target.value)} className="h-10 flex-1 border-zinc-200 bg-white font-mono text-[12px] text-zinc-900 dark:border-white/[0.06] dark:bg-white/[0.02] dark:text-white" placeholder="#c8ff00" />
                  </div>
                </Field>

                <Field label={`Speed — ${snakeSpeed.toFixed(1)}s / loop`}>
                  <input data-testid="campaign-snake-speed-slider" type="range" min="1" max="10" step="0.5" value={snakeSpeed} onChange={(e) => setSnakeSpeed(Number(e.target.value))} className="h-2 w-full cursor-pointer accent-[#c8ff00]" />
                </Field>

                <Field label={`Brightness — ${Math.round(snakeBrightness * 100)}%`}>
                  <input data-testid="campaign-snake-brightness-slider" type="range" min="0.2" max="1.5" step="0.05" value={snakeBrightness} onChange={(e) => setSnakeBrightness(Number(e.target.value))} className="h-2 w-full cursor-pointer accent-[#c8ff00]" />
                </Field>
              </div>

              <div className="flex items-center justify-center">
                <div
                  data-testid="campaign-snake-preview"
                  className={snakeEnabled ? "snake-border" : ""}
                  style={snakeEnabled ? { "--neon": neonColor, "--snake-speed": `${snakeSpeed}s`, "--snake-brightness": String(snakeBrightness) } : undefined}
                >
                  <div className="rounded-xl border border-zinc-200 bg-white px-5 py-4 text-center text-[12px] text-zinc-500 dark:border-white/[0.06] dark:bg-[#0a0a0a] dark:text-zinc-400">
                    Card edge preview
                  </div>
                </div>
              </div>
            </div>
          </Section>
        </div>

        {/* Sticky preview sidebar */}
        <aside className="lg:sticky lg:top-[80px] lg:h-fit">
          <LivePreview state={previewState} />
        </aside>
      </div>
    </div>
  );
}

// PlayCircle icon used in Tutorials section header — fallback if not imported
function PlayIconSafe(props) {
  return <Play {...props} />;
}
